import React from 'react';

import type { Theme } from '@mui/material';
import { AlineTooltip } from 'src/components';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

export const CITATION_INDEX_COUNTER_NAME = 'markdown-citation-index';

export const counterResetStyles = {
  counterReset: CITATION_INDEX_COUNTER_NAME,
} as const;

/** Short both ways: the card appears without a pause, and still survives the pointer's hop from badge to card. */
const CARD_DELAY_MS = 50;

/** Raises the card above a report cell popover, whose portaled date picker sits at modal + 2, and drops the plain tooltip's padding and width cap. */
const CARD_SLOT_PROPS = {
  popper: { sx: { zIndex: (theme: Theme) => theme.zIndex.modal + 3 } },
  tooltip: {
    sx: {
      boxShadow: (theme: Theme) => theme.appShadows.popper,
      maxWidth: 'none',
      p: 0,
    },
  },
} as const;

const useStyles = makeStyles()(theme => ({
  counter: {
    '&::before': {
      content: `counter(${CITATION_INDEX_COUNTER_NAME})`,
      counterIncrement: CITATION_INDEX_COUNTER_NAME,
    },
  },
  referenceLink: {
    ...getTypographyForSx(theme.typography.body1),
    alignItems: 'center',
    background: theme.palette.primary.main,
    borderRadius: 4,
    boxSizing: 'border-box',
    color: theme.palette.common.white,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 10,
    height: 16,
    padding: theme.spacing(0, 0.5),
    textDecoration: 'none',
    verticalAlign: 'middle',
  },
}));

interface CitationProps extends React.HTMLAttributes<HTMLAnchorElement> {
  /**
   * Rich hover card rendered in place of the plain `tooltip` string: it stays
   * open while the pointer is over it, and sits above a report cell popover.
   */
  card?: React.ReactNode;
  /** Wraps `card`, so a caller can mark it as inside its own portaled surface for an outside-click guard. */
  cardClassName?: string;
  /** Optional ID for the element */
  id?: string;
  /** Click handler for the citation */
  onClickCitation?: (e: React.MouseEvent) => void;
  /** Tooltip text to display on hover */
  tooltip?: string;
}

/**
 * IMPORTANT!
 *
 * When using this component, you must add the `counterResetStyles` to the
 * parent component in order for the CSS counter to work correctly.
 *
 * The `Citation` component is a pure presentational component for rendering a
 * citation and is used by both AI Chat and AI Report. Those components that use
 * this component are responsible for the business logic of the citation.
 */
export function Citation({
  card,
  cardClassName,
  id,
  onClickCitation,
  tooltip = 'View citation',
  ...rest
}: CitationProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <AlineTooltip
      arrow
      disableInteractive={!card}
      enterDelay={card ? CARD_DELAY_MS : undefined}
      leaveDelay={card ? CARD_DELAY_MS : 0}
      placement="top"
      slotProps={card ? CARD_SLOT_PROPS : undefined}
      title={card ? <div className={cardClassName}>{card}</div> : tooltip}
    >
      <a
        {...rest}
        // MUI's Tooltip only derives `aria-label` from a string `title`, and a card's is a node.
        {...(card ? { 'aria-label': tooltip } : {})}
        className={classes.referenceLink}
        id={id}
        rel="noopener noreferrer"
        target="_blank"
        onClick={onClickCitation}
      >
        {/* Uses a CSS counter to display the citation index */}
        <span className={classes.counter} />
      </a>
    </AlineTooltip>
  );
}
